import { useEffect, useMemo, useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { path } from "../data/base";
import type { CampaignTemplate, TacticDefinition, TacticIntervention } from "../data/cockpit-data";
import { useConfig } from "../data/queries";
import {
  TACTIC_CATALOGUE,
  TACTIC_INTERVENTION_TYPES,
  TACTIC_PARAM_DEFINITIONS,
  TACTIC_PARAM_TEMPLATE_DEFAULTS,
  buildTacticPayloadParams,
  campaignTemplateDerivedParams,
  isTacticInterventionType,
  isTacticSourcePath,
  validateTacticParams,
  type TacticInterventionType,
} from "../data/tactic-catalogue";

const SELECT_CLASS =
  "min-h-10 w-full rounded-ks-sm border border-ks-grey-3 bg-ks-surface px-3 text-sm text-ks-ink disabled:bg-muted";

type EditableIntervention = {
  intervention_type: TacticInterventionType;
  params: Record<string, string | number>;
};

function defaultParams(type: TacticInterventionType): Record<string, string | number> {
  return { ...TACTIC_PARAM_TEMPLATE_DEFAULTS[type] };
}

function editableIntervention(
  intervention?: TacticIntervention,
  defaultTemplateName = "",
  catalogue: readonly CampaignTemplate[] = [],
): EditableIntervention {
  const type =
    intervention && isTacticInterventionType(intervention.intervention_type)
      ? intervention.intervention_type
      : "create_campaign";
  const initial = defaultParams(type);
  for (const key of TACTIC_CATALOGUE[type].keys) {
    const value = intervention?.params[key];
    if (typeof value === "string" || typeof value === "number") initial[key] = value;
  }
  if (type === "create_campaign" && defaultTemplateName && !String(initial.template ?? "").trim()) {
    const derived = campaignTemplateDerivedParams(defaultTemplateName, catalogue);
    if (derived) Object.assign(initial, { template: defaultTemplateName, ...derived });
  }
  return { intervention_type: type, params: initial };
}

/**
 * The three fields a campaign template DETERMINES, shown read-only beside it.
 *
 * They are rendered rather than hidden because an operator picking a template
 * is choosing all four at once, and a template whose consequences are invisible
 * is a choice made blind. "From template" is stated on each, so nobody hunts
 * for the input that would let them change it.
 */
function CampaignTemplateFields({
  params,
  errors,
  catalogue,
  defaultName,
  loading,
  onTemplateChange,
}: {
  params: Record<string, string | number>;
  errors: Record<string, string>;
  catalogue: CampaignTemplate[];
  defaultName: string;
  loading: boolean;
  onTemplateChange: (value: string) => void;
}) {
  const templateName = String(params.template || defaultName);
  const derived = campaignTemplateDerivedParams(templateName, catalogue);
  const activeOptions = catalogue.filter((template) => !template.retired);
  const pinned = catalogue.find((template) => template.name === templateName);
  // A retired template stays selectable when it is the one already in use — the
  // alternative silently reassigns the tactic to a different template on open.
  const options = pinned?.retired ? [...activeOptions, pinned] : activeOptions;
  const fields = [
    ["campaign_type", "Campaign type"],
    ["funnel_stage", "Funnel stage"],
    ["placement", "Placement"],
  ] as const;

  return (
    <div className="grid gap-4 rounded-lg border border-ks-grey-3 p-4 sm:grid-cols-2">
      <label className="flex flex-col gap-1.5 text-[13px] font-medium text-muted-foreground sm:col-span-2">
        <span>
          Campaign template <span aria-hidden="true">*</span>
        </span>
        <select
          required
          className={SELECT_CLASS}
          value={templateName}
          disabled={loading || catalogue.length === 0}
          onChange={(event) => onTemplateChange(event.target.value)}
        >
          {(loading || catalogue.length === 0) && <option value="">Loading templates</option>}
          {options.map((option) => (
            <option key={option.name} value={option.name}>
              {option.name}
              {option.retired ? " (retired)" : ""}
            </option>
          ))}
        </select>
        {errors.template && <span className="text-xs text-ks-danger">{errors.template}</span>}
      </label>
      {fields.map(([key, label]) => (
        <div key={key} className="flex flex-col gap-1.5 rounded-lg border border-ks-grey-3 bg-muted/40 px-3 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[13px] font-medium text-muted-foreground">{label}</span>
            <Badge variant="outline">From template</Badge>
          </div>
          <span className="font-mono text-xs text-ks-ink">{derived?.[key] ?? "Unknown template"}</span>
          <p className="text-xs font-normal text-muted-foreground">
            Determined by the campaign template, not chosen separately.
          </p>
          {errors[key] && <span className="text-xs text-ks-danger">{errors[key]}</span>}
        </div>
      ))}
    </div>
  );
}

export type TacticEditorMode = "create" | "edit" | "duplicate";

/**
 * Author or amend one tactic definition.
 *
 * Every parameter this form can set is validated against the shipped
 * intervention catalogue BEFORE the write, and the catalogue is the data layer's
 * — not a second copy here. Two bindings are deliberately not editable: a
 * template-derived field belongs to the template, and an allocation-bound field
 * is set per item when the tactic runs. Both are shown with the reason stated
 * rather than omitted, so a missing input never reads as a missing feature.
 */
export function TacticEditor({
  tactic,
  mode,
  onCancel,
  onSaved,
}: {
  tactic: TacticDefinition | null;
  mode: TacticEditorMode;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const config = useConfig();
  const catalogue = useMemo(() => config.data?.campaign_template_catalogue ?? [], [config.data]);
  const activeCatalogue = catalogue.filter((template) => !template.retired);
  const configuredDefault = config.data?.campaign_template_default ?? "";
  const defaultTemplateName = activeCatalogue.some((template) => template.name === configuredDefault)
    ? configuredDefault
    : (activeCatalogue[0]?.name ?? "");
  const current = tactic?.versions.find((version) => version.is_current) ?? tactic?.versions[0];
  const [tacticId, setTacticId] = useState(mode === "edit" ? (tactic?.tactic_id ?? "") : "");
  const [name, setName] = useState(mode === "duplicate" ? `${tactic?.name ?? ""} copy` : (tactic?.name ?? ""));
  const [description, setDescription] = useState(
    mode === "duplicate" ? `Duplicated from ${tactic?.tactic_id ?? ""}` : (tactic?.description ?? ""),
  );
  const [interventions, setInterventions] = useState<EditableIntervention[]>(
    current?.interventions.length
      ? current.interventions.map((intervention) => editableIntervention(intervention))
      : [editableIntervention()],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const title =
    mode === "duplicate"
      ? `Duplicate ${tactic?.name ?? "tactic"}`
      : mode === "edit"
        ? `Edit ${tactic?.name ?? "tactic"}`
        : "Add tactic";
  const help =
    mode === "duplicate"
      ? "Create a new named template from this one. The copy starts at version 1 and is not linked to any signal."
      : mode === "edit"
        ? "Update tactic metadata or intervention template values."
        : "Create a versioned tactic definition from the intervention catalogue.";

  useEffect(() => {
    if (!defaultTemplateName) return;
    const derived = campaignTemplateDerivedParams(defaultTemplateName, catalogue);
    if (!derived) return;
    setInterventions((items) =>
      items.map((item) =>
        item.intervention_type === "create_campaign" && !String(item.params.template ?? "").trim()
          ? { ...item, params: { ...item.params, template: defaultTemplateName, ...derived } }
          : item,
      ),
    );
  }, [catalogue, defaultTemplateName]);

  function updateType(index: number, type: TacticInterventionType) {
    setInterventions((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index
          ? editableIntervention({ intervention_type: type, params: {} }, defaultTemplateName, catalogue)
          : item,
      ),
    );
  }

  function updateParam(index: number, key: string, value: string) {
    setInterventions((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, params: { ...item.params, [key]: value } } : item,
      ),
    );
  }

  function updateCampaignTemplate(index: number, template: string) {
    const derived = campaignTemplateDerivedParams(template, catalogue);
    if (!derived) {
      updateParam(index, "template", template);
      return;
    }
    setInterventions((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, params: { ...item.params, template, ...derived } } : item,
      ),
    );
  }

  async function submit() {
    const nextErrors: Record<string, string> = {};
    if (!tacticId.trim()) nextErrors.tactic_id = "Tactic ID is required.";
    if (!name.trim()) nextErrors.name = "Name is required.";
    if (mode === "duplicate" && !tactic) nextErrors.source_tactic_id = "Source tactic is required.";
    interventions.forEach((intervention, index) => {
      for (const [key, message] of Object.entries(
        validateTacticParams(intervention.intervention_type, intervention.params, catalogue),
      )) {
        nextErrors[`${index}:${key}`] = message;
      }
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const body =
        mode === "duplicate"
          ? {
              operation: mode,
              source_tactic_id: tactic?.tactic_id ?? "",
              tactic_id: tacticId.trim(),
              name: name.trim(),
              description: description.trim(),
            }
          : {
              operation: mode,
              tactic_id: tacticId.trim(),
              name: name.trim(),
              description: description.trim(),
              interventions: interventions.map((intervention) => ({
                intervention_type: intervention.intervention_type,
                params: buildTacticPayloadParams(intervention.intervention_type, intervention.params, catalogue),
              })),
            };
      const response = await fetch(path("/tactic"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
        detail?: string;
        version?: number;
        changed?: boolean;
        renamed?: boolean;
      };
      if (!response.ok) {
        setSubmitError(
          response.status === 403
            ? "You are not permitted to change tactic definitions."
            : (result.detail ?? result.error ?? "The tactic could not be saved."),
        );
        return;
      }
      // The message names what actually happened upstream, including the case
      // where nothing did: a silent "Saved" on a no-op write teaches an operator
      // that the button lies.
      if (mode === "duplicate") {
        onSaved(`Duplicated ${tactic?.name ?? "tactic"} as ${name.trim()} at version 1.`);
      } else if (mode === "create") {
        onSaved(`Created ${name.trim()} at version ${result.version ?? 1}.`);
      } else if (result.changed) {
        onSaved(`Saved ${name.trim()} as version ${result.version ?? tactic?.current_version ?? 1}.`);
      } else if (result.renamed) {
        onSaved(`Renamed to ${name.trim()}. Version ${result.version ?? tactic?.current_version ?? 1} unchanged.`);
      } else {
        onSaved("No changes to save.");
      }
    } catch {
      setSubmitError("The tactic could not be saved. Check the connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-semibold text-ks-ink">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{help}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Tactic ID" error={errors.tactic_id}>
            <Input
              className="min-h-10"
              value={tacticId}
              disabled={mode === "edit"}
              onChange={(event) => setTacticId(event.target.value)}
            />
          </Field>
          <Field label="Name" error={errors.name}>
            <Input className="min-h-10" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </div>
        <Field label="Description">
          <Textarea
            className="min-h-24"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-ks-ink">Interventions</h4>
            <Button
              variant="link"
              size="sm"
              onClick={() =>
                setInterventions((items) => [
                  ...items,
                  editableIntervention(undefined, defaultTemplateName, catalogue),
                ])
              }
            >
              Add intervention
            </Button>
          </div>
          {interventions.map((intervention, index) => (
            <div key={index} className="flex flex-col gap-4 rounded-lg border border-ks-grey-3 p-4">
              <div className="flex items-end gap-3">
                <label className="flex flex-1 flex-col gap-1.5 text-[13px] font-medium text-muted-foreground">
                  <span>Intervention type</span>
                  <select
                    className={SELECT_CLASS}
                    value={intervention.intervention_type}
                    onChange={(event) => updateType(index, event.target.value as TacticInterventionType)}
                  >
                    {TACTIC_INTERVENTION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {TACTIC_CATALOGUE[type].label}
                      </option>
                    ))}
                  </select>
                </label>
                {interventions.length > 1 && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() =>
                      setInterventions((items) => items.filter((_, itemIndex) => itemIndex !== index))
                    }
                  >
                    Remove
                  </Button>
                )}
              </div>

              {intervention.intervention_type === "create_campaign" && (
                <CampaignTemplateFields
                  params={intervention.params}
                  catalogue={catalogue}
                  defaultName={defaultTemplateName}
                  loading={config.isPending}
                  errors={Object.fromEntries(
                    TACTIC_CATALOGUE.create_campaign.keys.map((key) => [key, errors[`${index}:${key}`] ?? ""]),
                  )}
                  onTemplateChange={(value) => updateCampaignTemplate(index, value)}
                />
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                {TACTIC_CATALOGUE[intervention.intervention_type].keys
                  .filter((key) => TACTIC_CATALOGUE[intervention.intervention_type].bindings[key] !== "static")
                  .map((key) => {
                    const definition = TACTIC_PARAM_DEFINITIONS[key];
                    const binding = TACTIC_CATALOGUE[intervention.intervention_type].bindings[key];
                    const error = errors[`${index}:${key}`];
                    const templateDefault = TACTIC_PARAM_TEMPLATE_DEFAULTS[intervention.intervention_type][key];

                    if (binding === "allocation") {
                      return (
                        <div
                          key={key}
                          className="flex flex-col gap-1.5 rounded-lg border border-ks-grey-3 bg-muted/40 px-3 py-2.5"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="text-[13px] font-medium text-muted-foreground">
                              {definition.label}
                            </span>
                            <Badge variant="outline">Set at allocation</Badge>
                          </div>
                          <span className="font-mono text-xs text-ks-ink">
                            {String(intervention.params[key] ?? templateDefault)}
                          </span>
                          <p className="text-xs font-normal text-muted-foreground">
                            Bound per item when the tactic is allocated, not a template value.
                          </p>
                        </div>
                      );
                    }

                    // A source path (`item.foo`) is machine plumbing, not a value an
                    // operator typed. Showing it in the box would invite an edit that
                    // silently replaces a binding with a literal.
                    const displayedValue = isTacticSourcePath(intervention.params[key])
                      ? ""
                      : (intervention.params[key] ?? "");

                    return (
                      <Field key={key} label={definition.label} error={error}>
                        {definition.kind === "enum" ? (
                          <select
                            className={SELECT_CLASS}
                            value={String(displayedValue)}
                            onChange={(event) => updateParam(index, key, event.target.value)}
                          >
                            <option value="">{templateDefault}</option>
                            {definition.options?.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            className="min-h-10"
                            type={definition.kind === "number" ? "number" : "text"}
                            min={definition.min}
                            step={definition.step}
                            value={displayedValue}
                            placeholder={templateDefault}
                            onChange={(event) => updateParam(index, key, event.target.value)}
                          />
                        )}
                        <span className="text-xs font-normal text-muted-foreground">
                          Leave blank to use the machine default.
                        </span>
                      </Field>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        {submitError && (
          <p role="alert" className="text-sm font-medium text-ks-danger">
            {submitError}
          </p>
        )}
        <div className="flex justify-end gap-3 border-t border-ks-grey-3 pt-5">
          <Button variant="outline" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="lg" disabled={submitting} onClick={submit}>
            {submitting ? "Saving…" : mode === "duplicate" ? "Duplicate tactic" : mode === "edit" ? "Save" : "Create tactic"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5 text-[13px] font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
      {error && <span className="text-xs text-ks-danger">{error}</span>}
    </label>
  );
}
